import { metricsBus } from "@/common/buses";
import { useDeviceType } from "@/common/hooks";
import { useGetNichesSearches, useGetCreatorsSearches, useGetTagsSearches } from "@/common/queries/queries";
import { deviceTypeEnum } from "@/common/utils/device";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IUseSearchResults {
  searchValue: string;
}

const MAX_DESKTOP_ITEMS = 5;
const MAX_MOBILE_ITEMS = 3;
const DELAY = 500;

export const useSearchResults = ({ searchValue }: IUseSearchResults) => {
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const searchText = useMemo(() => encodeURI(searchValue), [searchValue]);
  const deviceType = useDeviceType();
  const isPhone = useMemo(() => deviceType === deviceTypeEnum.PHONE, [deviceType]);

  const { data: niches, isLoading: nichesLoading, isSuccess: nichesSuccess, isPending: nichesPending } = useGetNichesSearches(debouncedValue);
  const {
    data: creators,
    isLoading: creatorsLoading,
    isSuccess: creatorsSuccess,
    isPending: creatorsPending
  } = useGetCreatorsSearches(debouncedValue);
  const { data: tags, isLoading: tagsLoading, isSuccess: tagsSuccess, isPending: tagsPending } = useGetTagsSearches(debouncedValue);

  const hasSearchData = useMemo(() => nichesSuccess && creatorsSuccess && tagsSuccess, [nichesSuccess, creatorsSuccess, tagsSuccess]);

  const searchResults = useMemo(() => {
    const itemsLimit = isPhone ? MAX_MOBILE_ITEMS : MAX_DESKTOP_ITEMS;
    return {
      creators: creators?.items?.slice(0, itemsLimit) ?? [],
      niches: niches?.slice(0, itemsLimit) ?? [],
      tags: tags?.slice(0, itemsLimit) ?? [],
    };
  }, [creators, niches, tags, isPhone]);

  const totalCount = useMemo(() => {
    return (creators?.items?.length || 0) + (niches?.length || 0) + (tags?.length || 0);
  }, [creators, niches, tags]);

  const sendSearchGaEvent = useCallback((query: string, totalCount: number) => {
    metricsBus.emit("searched", { query, totalCount });
  }, []);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedValue(searchText);
    }, DELAY);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]);

  useEffect(() => {
    if (hasSearchData) {
      sendSearchGaEvent(searchText, totalCount);
    }
  }, [hasSearchData]);

  return {
    ...searchResults,
    creatorsLoading: creatorsLoading || creatorsPending,
    nichesLoading: nichesLoading || nichesPending,
    tagsLoading: tagsLoading || tagsPending,
  };
};
